import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, catchError, map, of, switchMap, take, tap } from 'rxjs';
import { AppUtilityService } from './app-utility.service';
import { apiPaths } from '../../app/core/constants/api.const';
import { selectStudy } from '../../app/state/selectors';
import { setAuditTrail } from '../../app/state/actions';
import { AuditTrailEntry } from '../models/study.model';

/**
 * Records a Print action against the current study. Shared by the print preview bar and the
 * older print menu so both write the same audit entry while the two coexist.
 */
@Injectable({ providedIn: 'root' })
export class PrintAuditService {
  constructor(
    private appUtilityService: AppUtilityService,
    private store: Store) { }

  /**
   * Always completes: a failed audit write must not stop the operator printing.
   * @param viewName Screen being printed, recorded as the entry's comment.
   */
  logPrint(viewName: string): Observable<void> {
    return this.logAction('Print', viewName);
  }

  /**
   * Records an Export action against the current study; always completes, like `logPrint`.
   * @param comment What was exported, recorded as the entry's comment.
   */
  logExport(comment: string): Observable<void> {
    return this.logAction('Export', comment);
  }

  private logAction(action: 'Print' | 'Export', comment: string): Observable<void> {
    return this.store.select(selectStudy).pipe(
      take(1),
      switchMap(study => {
        const payload = {
          run_name: study.studyName,
          action,
          comment
        };
        const url = this.appUtilityService.getAPI(apiPaths.study.auditTrail);
        return this.appUtilityService.post<typeof payload, { audit_trail: AuditTrailEntry[] }>(url, payload, true).pipe(
          catchError(() => of(null))
        );
      }),
      tap(response => {
        if (response?.audit_trail) {
          this.store.dispatch(setAuditTrail({ auditTrail: response.audit_trail }));
        }
      }),
      map(() => undefined)
    );
  }
}
